import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  Assessment as AssessmentIcon,
  Build as BuildIcon,
  InfoOutlined as OverviewIcon,
  Memory as ComputingIcon,
  NavigateBefore as BackIcon,
  NavigateNext as NextIcon,
  People as PeopleIcon,
  Storage as StorageIcon,
  AccountTree as OperationalDataIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import api from "../utils/api";
import {
  APPLICABLE_OPTIONS,
  SYSTEM_TYPES,
  AI_USE_TYPES,
  VALUE_CHAIN_ROLES,
  BUSINESS_AREAS,
  LIFECYCLE_STAGES,
  TRAINING_DATA_CATEGORIES,
  OPERATIONAL_DATA_TYPES,
  PERSONAL_DATA_OPTIONS,
  CLIENT_CONFIDENTIAL_OPTIONS,
  PROVISIONING_OPTIONS,
  INFRASTRUCTURE_PROVIDERS,
  HUMAN_OVERSIGHT_OPTIONS,
  INCIDENT_NOTIFY_ROLES,
  GOVERNANCE_ROLE_OPTIONS,
  OWNER_ROLE_SUGGESTIONS,
  ASSESSMENT_STATUS_OPTIONS,
  APPROVAL_STATUS_OPTIONS,
} from "../constants/aiSystemsOptions";
import {
  AI_SYSTEM_TAB_COUNT,
  firstTabWithErrors,
} from "../utils/aiSystemFormTabs";
import { isBlank, mergeAiSystemForm } from "../utils/aiSystemFormValues";
import { formatLastUpdatedBy } from "../utils/aiSystemActivity";

const emptyForm = {
  name: "",
  providerId: null,
  systemType: "",
  useCase: "",
  aiUseTypes: [],
  ownerName: "",
  ownerId: "",
  valueChainRoles: [],
  businessAreas: [],
  clientId: null,
  lifecycleStage: "",
  trainingDataApplicable: "No",
  trainingDataProvenance: "",
  trainingDataLastUpdated: null,
  trainingDataCategories: [],
  dataLabellingApproach: "",
  trainingDataQualityMeasures: "",
  trainingDataRetention: "",
  potentialBiasIssues: "",
  dataPreparation: "",
  operationalDataApplicable: "No",
  operationalDataTypes: [],
  dataInputs: "",
  personalData: "No",
  clientConfidentialData: "No",
  thirdPartyClientDataDetails: "",
  dataOutputs: "",
  dataFlows: "",
  operationalDataRetention: "",
  dataProtectionCompliance: "",
  toolingApplicable: "No",
  algorithmType: "",
  dataConditioningTools: "",
  optimisationMethods: "",
  developmentBuildTools: "",
  provisioning: [],
  infrastructureProviders: [],
  integrationPoints: "",
  softwareHardwareDependencies: "",
  architectureDocsRef: "",
  computingApplicable: "No",
  minimumOperationalRequirements: "",
  hostingLocation: "",
  networkStorage: "",
  availabilityResilience: "",
  environmentalImpact: "",
  humanResourcesApplicable: "Yes",
  humanOversight: "Not Applicable",
  humanOversightDetails: "",
  incidentNotifyRoles: [],
  operationalRoles: [],
  technicalDevelopmentRoles: [],
  governanceComplianceRoles: [],
  trainingCompetenceRequirements: "",
  safetySecurityPrivacyResponsibilities: "",
  authorityToSuspend: [],
  finalAuthorisation: [],
  riskAssessment: "Not Assessed",
  riskAssessmentRef: "",
  aegisRiskRef: "",
  aiImpactAssessment: "Not Assessed",
  impactAssessmentRef: "",
  approvalStatus: "Under Assessment",
  approvedByName: "",
  approvedById: "",
  approvalDate: null,
  conditionsRestrictions: "",
  reviewDate: null,
  recordStatus: "Draft",
  otherAiUseType: "",
  otherBusinessArea: "",
  otherSystemType: "",
  otherInfrastructureProvider: "",
  otherProvisioning: "",
  otherTrainingDataCategory: "",
  otherIncidentNotifyRole: "",
  otherOperationalRole: "",
  otherTechnicalDevelopmentRole: "",
  otherGovernanceComplianceRole: "",
  trainingDataNoRationale: "",
  operationalDataNoRationale: "",
  toolingNoRationale: "",
  computingNoRationale: "",
  humanResourcesNoRationale: "",
};

const sectionVisible = (applicable) =>
  applicable === "Yes" || applicable === "Partial";

const MultiSelect = ({
  label,
  options,
  value,
  onChange,
  error,
  helperText,
  required = false,
}) => (
  <Autocomplete
    multiple
    disableCloseOnSelect
    options={options}
    value={value || []}
    onChange={(_, next) => onChange(next)}
    renderTags={(tagValue, getTagProps) =>
      tagValue.map((option, index) => (
        <Chip label={option} {...getTagProps({ index })} size="small" />
      ))
    }
    renderInput={(params) => (
      <TextField
        {...params}
        required={required}
        label={label}
        error={Boolean(error)}
        helperText={helperText}
      />
    )}
  />
);

const mapDirectoryPerson = (u) => {
  const email = u.email || u.mail || u.userPrincipalName || "";
  const name =
    u.name ||
    u.displayName ||
    `${u.first_name || u.givenName || ""} ${u.last_name || u.surname || ""}`.trim() ||
    email ||
    "Unknown";
  return {
    id: u.id || u.userId || email || name,
    name,
    email,
    label: email ? `${name} (${email})` : name,
  };
};

const PeopleMultiSelect = ({
  label,
  options,
  value,
  onChange,
  error,
  helperText,
  loading = false,
}) => (
  <Autocomplete
    multiple
    freeSolo
    openOnFocus
    autoHighlight
    disableCloseOnSelect
    filterSelectedOptions
    loading={loading}
    options={options}
    value={Array.isArray(value) ? value : []}
    getOptionLabel={(option) => {
      if (typeof option === "string") return option;
      return option?.label || option?.name || "";
    }}
    isOptionEqualToValue={(option, selected) => {
      const selectedName =
        typeof selected === "string" ? selected : selected?.name;
      return (
        option?.name === selectedName ||
        option?.label === selectedName ||
        option?.email === selectedName
      );
    }}
    filterOptions={(opts, state) => {
      const q = String(state.inputValue || "")
        .trim()
        .toLowerCase();
      if (!q) return opts;
      return opts.filter(
        (o) =>
          String(o.name || "")
            .toLowerCase()
            .includes(q) ||
          String(o.email || "")
            .toLowerCase()
            .includes(q) ||
          String(o.label || "")
            .toLowerCase()
            .includes(q),
      );
    }}
    onChange={(_, next) =>
      onChange(
        next.map((item) =>
          typeof item === "string" ? item.trim() : item.name || item.label,
        ),
      )
    }
    renderOption={(props, option) => (
      <li {...props} key={option.id || option.email || option.name}>
        <Box display="flex" flexDirection="column" minWidth={0}>
          <Typography variant="body2" noWrap>
            {option.name}
          </Typography>
          {option.email ? (
            <Typography variant="caption" color="text.secondary" noWrap>
              {option.email}
            </Typography>
          ) : null}
        </Box>
      </li>
    )}
    renderTags={(tagValue, getTagProps) =>
      tagValue.map((option, index) => (
        <Chip
          label={typeof option === "string" ? option : option.name}
          {...getTagProps({ index })}
          size="small"
        />
      ))
    }
    componentsProps={{
      popper: {
        sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
      },
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        error={Boolean(error)}
        helperText={
          helperText ||
          (loading
            ? "Loading directory…"
            : options.length
              ? "Type to search the directory, or press Enter to add a name"
              : "Directory unavailable — type a name and press Enter")
        }
      />
    )}
  />
);

const AiSystemForm = ({ aiSystem, onSubmit, onCancel }) => {
  const [tab, setTab] = useState(0);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [people, setPeople] = useState([]);
  const [peopleLoading, setPeopleLoading] = useState(true);

  useEffect(() => {
    const loadDirectoryPeople = async () => {
      setPeopleLoading(true);
      try {
        try {
          const configsRes = await api.get("/api/azure-tenant/list");
          const configs = configsRes.data?.configs || [];
          const config =
            configs.find((c) => c.is_active && c.test_status === "success") ||
            configs.find((c) => c.is_active) ||
            configs[0];
          if (config?.id) {
            const usersRes = await api.get(
              "/api/asset-register/users/entraid",
              { params: { configId: config.id } },
            );
            const entraUsers = usersRes.data?.users || [];
            if (Array.isArray(entraUsers) && entraUsers.length > 0) {
              setPeople(entraUsers.map(mapDirectoryPerson));
              return;
            }
          }
        } catch (err) {
          console.warn("Failed to load Entra directory users:", err);
        }

        // Fallback: Pulse app users if Entra directory is unavailable
        const localRes = await api.get("/api/users");
        const localUsers = Array.isArray(localRes.data) ? localRes.data : [];
        setPeople(
          localUsers.map((u) =>
            mapDirectoryPerson({
              id: u.id,
              name:
                `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
                u.username ||
                u.email,
              email: u.email,
            }),
          ),
        );
      } catch (fallbackErr) {
        console.warn("Failed to load directory people:", fallbackErr);
        setPeople([]);
      } finally {
        setPeopleLoading(false);
      }
    };

    const loadLookups = async () => {
      try {
        const [clientsRes, suppliersRes] = await Promise.all([
          api.get("/api/clients/list"),
          api.get("/api/suppliers/list"),
        ]);
        setClients(clientsRes.data.clients || []);
        setSuppliers(suppliersRes.data.suppliers || []);
      } catch (err) {
        console.warn("Failed to load AI system lookups", err);
      }
      await loadDirectoryPeople();
    };
    loadLookups();
  }, []);

  useEffect(() => {
    setTab(0);
    setErrors({});
    if (!aiSystem) {
      setFormData({ ...emptyForm });
      return;
    }
    setFormData(
      mergeAiSystemForm(emptyForm, {
        ...aiSystem,
        providerId: aiSystem.providerId ?? null,
        clientId: aiSystem.clientId ?? null,
        trainingDataLastUpdated: aiSystem.trainingDataLastUpdated
          ? new Date(aiSystem.trainingDataLastUpdated)
          : null,
        approvalDate: aiSystem.approvalDate
          ? new Date(aiSystem.approvalDate)
          : null,
        reviewDate: aiSystem.reviewDate ? new Date(aiSystem.reviewDate) : null,
      }),
    );
  }, [aiSystem]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === formData.clientId) || null,
    [clients, formData.clientId],
  );
  const selectedProvider = useMemo(
    () => suppliers.find((s) => s.id === formData.providerId) || null,
    [suppliers, formData.providerId],
  );
  const ownerOptions = useMemo(
    () => [
      ...OWNER_ROLE_SUGGESTIONS.map((role) => ({
        id: `role:${role}`,
        label: role,
        name: role,
        email: "",
      })),
      ...people,
    ],
    [people],
  );

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const next = {};
    if (isBlank(formData.name)) next.name = "Required";
    if (!formData.providerId) next.providerId = "Required";
    if (!formData.systemType) next.systemType = "Required";
    if (isBlank(formData.useCase)) next.useCase = "Required";
    if (!formData.aiUseTypes?.length) next.aiUseTypes = "Required";
    if (isBlank(formData.ownerName)) next.ownerName = "Required";
    if (!formData.valueChainRoles?.length) next.valueChainRoles = "Required";
    if (!formData.businessAreas?.length) next.businessAreas = "Required";
    if (!formData.clientId) next.clientId = "Required";
    if (!formData.lifecycleStage) next.lifecycleStage = "Required";
    if (
      formData.riskAssessment === "Completed" &&
      !formData.riskAssessmentRef
    ) {
      next.riskAssessmentRef = "Required when Completed";
    }
    if (
      formData.clientConfidentialData !== "No" &&
      !formData.thirdPartyClientDataDetails?.trim()
    ) {
      next.thirdPartyClientDataDetails = "Required";
    }
    const approved = [
      "Approved",
      "Approved with Conditions",
      "Restricted",
    ].includes(formData.approvalStatus);
    if (approved) {
      if (!formData.approvedByName?.trim()) next.approvedByName = "Required";
      if (!formData.approvalDate) next.approvalDate = "Required";
      if (!formData.reviewDate) next.reviewDate = "Required";
      if (!formData.operationalRoles?.length) {
        next.operationalRoles = "Required once approved";
      }
      if (!formData.governanceComplianceRoles?.length) {
        next.governanceComplianceRoles = "Required once approved";
      }
      if (isBlank(formData.trainingCompetenceRequirements)) {
        next.trainingCompetenceRequirements = "Required once approved";
      }
    }
    if (
      ["Approved with Conditions", "Restricted"].includes(
        formData.approvalStatus,
      ) &&
      !formData.conditionsRestrictions?.trim()
    ) {
      next.conditionsRestrictions = "Required";
    }
    if (
      formData.aiUseTypes?.includes("Other") &&
      isBlank(formData.otherAiUseType)
    ) {
      next.otherAiUseType = "Describe the other AI use type";
    }
    if (
      formData.businessAreas?.includes("Other") &&
      isBlank(formData.otherBusinessArea)
    ) {
      next.otherBusinessArea = "Describe the other business area";
    }
    if (formData.systemType === "Other" && isBlank(formData.otherSystemType)) {
      next.otherSystemType = "Describe the other system type";
    }
    if (
      formData.infrastructureProviders?.includes("Other") &&
      isBlank(formData.otherInfrastructureProvider)
    ) {
      next.otherInfrastructureProvider =
        "Describe the other infrastructure / cloud provider";
    }
    if (
      formData.provisioning?.includes("Other") &&
      isBlank(formData.otherProvisioning)
    ) {
      next.otherProvisioning = "Describe the other provisioning option";
    }
    if (
      formData.trainingDataCategories?.includes("Other") &&
      isBlank(formData.otherTrainingDataCategory)
    ) {
      next.otherTrainingDataCategory =
        "Describe the other training data category";
    }
    if (
      formData.incidentNotifyRoles?.includes("Other") &&
      isBlank(formData.otherIncidentNotifyRole)
    ) {
      next.otherIncidentNotifyRole =
        "Describe the other incident notification role";
    }
    if (
      formData.operationalRoles?.includes("Other") &&
      isBlank(formData.otherOperationalRole)
    ) {
      next.otherOperationalRole = "Describe the other operational role";
    }
    if (
      formData.technicalDevelopmentRoles?.includes("Other") &&
      isBlank(formData.otherTechnicalDevelopmentRole)
    ) {
      next.otherTechnicalDevelopmentRole =
        "Describe the other technical / development role";
    }
    if (
      formData.governanceComplianceRoles?.includes("Other") &&
      isBlank(formData.otherGovernanceComplianceRole)
    ) {
      next.otherGovernanceComplianceRole =
        "Describe the other governance / compliance role";
    }
    const noRationaleChecks = [
      ["trainingDataApplicable", "trainingDataNoRationale"],
      ["operationalDataApplicable", "operationalDataNoRationale"],
      ["toolingApplicable", "toolingNoRationale"],
      ["computingApplicable", "computingNoRationale"],
      ["humanResourcesApplicable", "humanResourcesNoRationale"],
    ];
    noRationaleChecks.forEach(([applicableField, rationaleField]) => {
      if (
        formData[applicableField] === "No" &&
        isBlank(formData[rationaleField])
      ) {
        next[rationaleField] = "Required when Not Applicable";
      }
    });
    setErrors(next);
    return next;
  };

  const buildPayload = () => {
    const { incidentNotifyPeople: _removed, ...rest } = formData;
    return {
      ...rest,
      trainingDataLastUpdated: formData.trainingDataLastUpdated
        ? formData.trainingDataLastUpdated.toISOString().split("T")[0]
        : null,
      approvalDate: formData.approvalDate
        ? formData.approvalDate.toISOString().split("T")[0]
        : null,
      reviewDate: formData.reviewDate
        ? formData.reviewDate.toISOString().split("T")[0]
        : null,
    };
  };

  const persist = async (recordStatus) => {
    if (recordStatus === "Draft") {
      if (isBlank(formData.name)) {
        setErrors({ name: "Required to save a draft" });
        setTab(0);
        return;
      }
    } else {
      const next = validate();
      if (Object.keys(next).length > 0) {
        setTab(firstTabWithErrors(next));
        return;
      }
    }
    setSubmitting(true);
    try {
      await onSubmit({ ...buildPayload(), recordStatus });
    } catch (err) {
      const apiErrors = err.response?.data?.errors;
      if (Array.isArray(apiErrors)) {
        const mapped = {};
        apiErrors.forEach((item) => {
          if (item.path) mapped[item.path] = item.msg;
        });
        setErrors(mapped);
        setTab(firstTabWithErrors(mapped));
      } else {
        setErrors({
          name: err.response?.data?.error || "Failed to save AI system",
        });
        setTab(0);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await persist("Active");
  };

  const handleSaveDraft = async () => {
    await persist("Draft");
  };

  return (
      <Box
        component="form"
        onSubmit={handleSubmit}
        display="flex"
        flexDirection="column"
        height="100%"
        minHeight={0}
        mt={1}
      >
        {aiSystem?.systemCode && (
          <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
            ID: {aiSystem.systemCode}
            {aiSystem.recordStatus === "Draft" ? " · Draft" : ""}
          </Typography>
        )}
        {formatLastUpdatedBy(
          aiSystem?.lastUpdatedByName,
          aiSystem?.updatedAt,
        ) ? (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mb={1}
          >
            Last updated by{" "}
            {formatLastUpdatedBy(
              aiSystem?.lastUpdatedByName,
              aiSystem?.updatedAt,
            )}
          </Typography>
        ) : null}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab icon={<OverviewIcon />} iconPosition="start" label="Overview" />
          <Tab
            icon={<StorageIcon />}
            iconPosition="start"
            label="Training Data"
          />
          <Tab
            icon={<OperationalDataIcon />}
            iconPosition="start"
            label="Operational Data"
          />
          <Tab icon={<BuildIcon />} iconPosition="start" label="Tooling" />
          <Tab
            icon={<ComputingIcon />}
            iconPosition="start"
            label="Computing"
          />
          <Tab
            icon={<PeopleIcon />}
            iconPosition="start"
            label="Human Resources"
          />
          <Tab
            icon={<AssessmentIcon />}
            iconPosition="start"
            label="Assessment"
          />
        </Tabs>

        <Box flex={1} minHeight={0} overflow="auto">
          <Box pt={2.5} pb={1} pr={0.5}>
            {tab === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="AI System / Tool"
                    value={formData.name || ""}
                    onChange={(e) => setField("name", e.target.value)}
                    error={Boolean(errors.name)}
                    helperText={
                      errors.name ||
                      "Common name used to refer to this AI system or tool"
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={suppliers}
                    getOptionLabel={(o) => o.supplierCompany || ""}
                    value={selectedProvider}
                    onChange={(_, v) => setField("providerId", v?.id || null)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Provider (Supplier)"
                        error={Boolean(errors.providerId)}
                        helperText={
                          errors.providerId ||
                          "Supplier or vendor that provides the system"
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    required
                    error={Boolean(errors.systemType)}
                  >
                    <InputLabel>System type</InputLabel>
                    <Select
                      label="System type"
                      value={formData.systemType}
                      onChange={(e) => setField("systemType", e.target.value)}
                    >
                      {SYSTEM_TYPES.map((o) => (
                        <MenuItem key={o} value={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {errors.systemType ||
                        "How the system was obtained or built"}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                {formData.systemType === "Other" && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Other system type"
                      value={formData.otherSystemType || ""}
                      onChange={(e) =>
                        setField("otherSystemType", e.target.value)
                      }
                      error={Boolean(errors.otherSystemType)}
                      helperText={
                        errors.otherSystemType ||
                        "Describe the system type when Other is selected"
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <FormControl
                    fullWidth
                    required
                    error={Boolean(errors.lifecycleStage)}
                  >
                    <InputLabel>Lifecycle stage</InputLabel>
                    <Select
                      label="Lifecycle stage"
                      value={formData.lifecycleStage}
                      onChange={(e) =>
                        setField("lifecycleStage", e.target.value)
                      }
                    >
                      {LIFECYCLE_STAGES.map((o) => (
                        <MenuItem key={o} value={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {errors.lifecycleStage ||
                        "Current stage in the system lifecycle"}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    minRows={2}
                    label="Use case / purpose"
                    value={formData.useCase || ""}
                    onChange={(e) => setField("useCase", e.target.value)}
                    error={Boolean(errors.useCase)}
                    helperText={
                      errors.useCase ||
                      "What the system is used for and the intended outcome"
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MultiSelect
                    required
                    label="AI use types"
                    options={AI_USE_TYPES}
                    value={formData.aiUseTypes}
                    onChange={(v) => setField("aiUseTypes", v)}
                    error={errors.aiUseTypes}
                    helperText={
                      errors.aiUseTypes ||
                      "Select all that apply; menu stays open for multiple picks"
                    }
                  />
                </Grid>
                {formData.aiUseTypes?.includes("Other") && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Other AI use type"
                      value={formData.otherAiUseType || ""}
                      onChange={(e) =>
                        setField("otherAiUseType", e.target.value)
                      }
                      error={Boolean(errors.otherAiUseType)}
                      helperText={
                        errors.otherAiUseType ||
                        "Describe the AI use type when Other is selected"
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    freeSolo
                    options={ownerOptions}
                    getOptionLabel={(o) =>
                      typeof o === "string" ? o : o.label || ""
                    }
                    value={formData.ownerName || ""}
                    onChange={(_, v) => {
                      if (typeof v === "string") {
                        setField("ownerName", v);
                        setField("ownerId", "");
                      } else {
                        setField("ownerName", v?.label || "");
                        setField("ownerId", v?.id || "");
                      }
                    }}
                    onInputChange={(_, v) => setField("ownerName", v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Owner"
                        error={Boolean(errors.ownerName)}
                        helperText={
                          errors.ownerName ||
                          "Person or accountability role (e.g. InfoSec Director)"
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MultiSelect
                    required
                    label="Value chain roles"
                    options={VALUE_CHAIN_ROLES}
                    value={formData.valueChainRoles}
                    onChange={(v) => setField("valueChainRoles", v)}
                    error={errors.valueChainRoles}
                    helperText={
                      errors.valueChainRoles ||
                      "Nikao’s role(s) in the AI value chain for this system"
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <MultiSelect
                    required
                    label="Business area / function"
                    options={BUSINESS_AREAS}
                    value={formData.businessAreas}
                    onChange={(v) => setField("businessAreas", v)}
                    error={errors.businessAreas}
                    helperText={
                      errors.businessAreas ||
                      "Business area(s) that use or own this system"
                    }
                  />
                </Grid>
                {formData.businessAreas?.includes("Other") && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Other business area"
                      value={formData.otherBusinessArea || ""}
                      onChange={(e) =>
                        setField("otherBusinessArea", e.target.value)
                      }
                      error={Boolean(errors.otherBusinessArea)}
                      helperText={
                        errors.otherBusinessArea ||
                        "Describe the business area when Other is selected"
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={clients}
                    getOptionLabel={(o) => o.name || ""}
                    value={selectedClient}
                    onChange={(_, v) => setField("clientId", v?.id || null)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Client / Internal context"
                        error={Boolean(errors.clientId)}
                        helperText={
                          errors.clientId ||
                          "Client engagement or internal Nikao context"
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>
            )}

            {tab === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Training data applicable?</InputLabel>
                    <Select
                      label="Training data applicable?"
                      value={formData.trainingDataApplicable}
                      onChange={(e) =>
                        setField("trainingDataApplicable", e.target.value)
                      }
                    >
                      {APPLICABLE_OPTIONS.map((o) => (
                        <MenuItem key={o} value={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {formData.trainingDataApplicable === "No" && (
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      minRows={2}
                      label="Rationale (training data not applicable)"
                      value={formData.trainingDataNoRationale || ""}
                      onChange={(e) =>
                        setField("trainingDataNoRationale", e.target.value)
                      }
                      error={Boolean(errors.trainingDataNoRationale)}
                      helperText={
                        errors.trainingDataNoRationale ||
                        "Explain why training data does not apply"
                      }
                    />
                  </Grid>
                )}
                {sectionVisible(formData.trainingDataApplicable) && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Training data provenance"
                        value={formData.trainingDataProvenance}
                        onChange={(e) =>
                          setField("trainingDataProvenance", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Training data last updated"
                        value={formData.trainingDataLastUpdated}
                        onChange={(v) => setField("trainingDataLastUpdated", v)}
                        slotProps={{ textField: { fullWidth: true } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <MultiSelect
                        label="Training data categories"
                        options={TRAINING_DATA_CATEGORIES}
                        value={formData.trainingDataCategories}
                        onChange={(v) => setField("trainingDataCategories", v)}
                      />
                    </Grid>
                    {formData.trainingDataCategories?.includes("Other") && (
                      <Grid item xs={12} md={8}>
                        <TextField
                          fullWidth
                          required
                          label="Other training data category"
                          value={formData.otherTrainingDataCategory || ""}
                          onChange={(e) =>
                            setField("otherTrainingDataCategory", e.target.value)
                          }
                          error={Boolean(errors.otherTrainingDataCategory)}
                          helperText={
                            errors.otherTrainingDataCategory ||
                            "Describe the category when Other is selected"
                          }
                        />
                      </Grid>
                    )}
                    {[
                      ["dataLabellingApproach", "Data labelling approach"],
                      ["trainingDataQualityMeasures", "Quality measures"],
                      ["trainingDataRetention", "Retention & disposal"],
                      ["potentialBiasIssues", "Potential bias issues"],
                      ["dataPreparation", "Data preparation"],
                    ].map(([field, label]) => (
                      <Grid item xs={12} key={field}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label={label}
                          value={formData[field]}
                          onChange={(e) => setField(field, e.target.value)}
                        />
                      </Grid>
                    ))}
                  </>
                )}
              </Grid>
            )}

            {tab === 2 && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Operational data applicable?</InputLabel>
                    <Select
                      label="Operational data applicable?"
                      value={formData.operationalDataApplicable}
                      onChange={(e) =>
                        setField("operationalDataApplicable", e.target.value)
                      }
                    >
                      {APPLICABLE_OPTIONS.map((o) => (
                        <MenuItem key={o} value={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {formData.operationalDataApplicable === "No" && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      minRows={2}
                      label="Rationale (operational data not applicable)"
                      value={formData.operationalDataNoRationale || ""}
                      onChange={(e) =>
                        setField("operationalDataNoRationale", e.target.value)
                      }
                      error={Boolean(errors.operationalDataNoRationale)}
                      helperText={
                        errors.operationalDataNoRationale ||
                        "Explain why operational data does not apply"
                      }
                    />
                  </Grid>
                )}
                {sectionVisible(formData.operationalDataApplicable) && (
                  <>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Personal data?</InputLabel>
                        <Select
                          label="Personal data?"
                          value={formData.personalData}
                          onChange={(e) =>
                            setField("personalData", e.target.value)
                          }
                        >
                          {PERSONAL_DATA_OPTIONS.map((o) => (
                            <MenuItem key={o} value={o}>
                              {o}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Client / confidential data?</InputLabel>
                        <Select
                          label="Client / confidential data?"
                          value={formData.clientConfidentialData}
                          onChange={(e) =>
                            setField("clientConfidentialData", e.target.value)
                          }
                        >
                          {CLIENT_CONFIDENTIAL_OPTIONS.map((o) => (
                            <MenuItem key={o} value={o}>
                              {o}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <MultiSelect
                        label="Data / information used"
                        options={OPERATIONAL_DATA_TYPES}
                        value={formData.operationalDataTypes}
                        onChange={(v) => setField("operationalDataTypes", v)}
                      />
                    </Grid>
                    {[
                      ["dataInputs", "Data inputs"],
                      [
                        "thirdPartyClientDataDetails",
                        "Third-party / client data details",
                      ],
                      ["dataOutputs", "Data outputs"],
                      ["dataFlows", "Data flows"],
                      ["operationalDataRetention", "Retention & disposal"],
                      [
                        "dataProtectionCompliance",
                        "Data protection compliance",
                      ],
                    ].map(([field, label]) => (
                      <Grid item xs={12} key={field}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label={label}
                          value={formData[field]}
                          onChange={(e) => setField(field, e.target.value)}
                          error={Boolean(errors[field])}
                          helperText={errors[field]}
                        />
                      </Grid>
                    ))}
                  </>
                )}
              </Grid>
            )}

            {tab === 3 && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Tooling applicable?</InputLabel>
                    <Select
                      label="Tooling applicable?"
                      value={formData.toolingApplicable}
                      onChange={(e) =>
                        setField("toolingApplicable", e.target.value)
                      }
                    >
                      {APPLICABLE_OPTIONS.map((o) => (
                        <MenuItem key={o} value={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {formData.toolingApplicable === "No" && (
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      minRows={2}
                      label="Rationale (tooling not applicable)"
                      value={formData.toolingNoRationale || ""}
                      onChange={(e) =>
                        setField("toolingNoRationale", e.target.value)
                      }
                      error={Boolean(errors.toolingNoRationale)}
                      helperText={
                        errors.toolingNoRationale ||
                        "Explain why tooling does not apply"
                      }
                    />
                  </Grid>
                )}
                {sectionVisible(formData.toolingApplicable) && (
                  <>
                    {[
                      ["algorithmType", "Algorithm type / model architecture"],
                      ["dataConditioningTools", "Data conditioning tools"],
                      ["optimisationMethods", "Optimisation methods"],
                      ["developmentBuildTools", "Development & build tools"],
                      ["integrationPoints", "Integration points"],
                      [
                        "softwareHardwareDependencies",
                        "Software & hardware dependencies",
                      ],
                      [
                        "architectureDocsRef",
                        "Architecture / technical documentation ref.",
                      ],
                    ].map(([field, label]) => (
                      <Grid item xs={12} key={field}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label={label}
                          value={formData[field]}
                          onChange={(e) => setField(field, e.target.value)}
                        />
                      </Grid>
                    ))}
                    <Grid item xs={12} md={6}>
                      <MultiSelect
                        label="Provisioning & deployment"
                        options={PROVISIONING_OPTIONS}
                        value={formData.provisioning}
                        onChange={(v) => setField("provisioning", v)}
                      />
                    </Grid>
                    {formData.provisioning?.includes("Other") && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Other provisioning"
                          value={formData.otherProvisioning || ""}
                          onChange={(e) =>
                            setField("otherProvisioning", e.target.value)
                          }
                          error={Boolean(errors.otherProvisioning)}
                          helperText={
                            errors.otherProvisioning ||
                            "Describe provisioning when Other is selected"
                          }
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <MultiSelect
                        label="Infrastructure / cloud provider"
                        options={INFRASTRUCTURE_PROVIDERS}
                        value={formData.infrastructureProviders}
                        onChange={(v) => setField("infrastructureProviders", v)}
                      />
                    </Grid>
                    {formData.infrastructureProviders?.includes("Other") && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Other infrastructure / cloud provider"
                          value={formData.otherInfrastructureProvider || ""}
                          onChange={(e) =>
                            setField(
                              "otherInfrastructureProvider",
                              e.target.value,
                            )
                          }
                          error={Boolean(errors.otherInfrastructureProvider)}
                          helperText={
                            errors.otherInfrastructureProvider ||
                            "Describe the provider when Other is selected"
                          }
                        />
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            )}

            {tab === 4 && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Computing resources applicable?</InputLabel>
                    <Select
                      label="Computing resources applicable?"
                      value={formData.computingApplicable}
                      onChange={(e) =>
                        setField("computingApplicable", e.target.value)
                      }
                    >
                      {APPLICABLE_OPTIONS.map((o) => (
                        <MenuItem key={o} value={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {formData.computingApplicable === "No" && (
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      minRows={2}
                      label="Rationale (computing not applicable)"
                      value={formData.computingNoRationale || ""}
                      onChange={(e) =>
                        setField("computingNoRationale", e.target.value)
                      }
                      error={Boolean(errors.computingNoRationale)}
                      helperText={
                        errors.computingNoRationale ||
                        "Explain why computing resources do not apply"
                      }
                    />
                  </Grid>
                )}
                {sectionVisible(formData.computingApplicable) &&
                  [
                    [
                      "minimumOperationalRequirements",
                      "Minimum operational requirements",
                    ],
                    ["hostingLocation", "Hosting location"],
                    ["networkStorage", "Network & storage"],
                    ["availabilityResilience", "Availability & resilience"],
                    ["environmentalImpact", "Environmental impact"],
                  ].map(([field, label]) => (
                    <Grid item xs={12} key={field}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label={label}
                        value={formData[field]}
                        onChange={(e) => setField(field, e.target.value)}
                      />
                    </Grid>
                  ))}
              </Grid>
            )}

            {tab === 5 && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Human resources applicable?</InputLabel>
                    <Select
                      label="Human resources applicable?"
                      value={formData.humanResourcesApplicable}
                      onChange={(e) =>
                        setField("humanResourcesApplicable", e.target.value)
                      }
                    >
                      {APPLICABLE_OPTIONS.map((o) => (
                        <MenuItem key={o} value={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {formData.humanResourcesApplicable === "No" && (
                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      required
                      multiline
                      minRows={2}
                      label="Rationale (human resources not applicable)"
                      value={formData.humanResourcesNoRationale || ""}
                      onChange={(e) =>
                        setField("humanResourcesNoRationale", e.target.value)
                      }
                      error={Boolean(errors.humanResourcesNoRationale)}
                      helperText={
                        errors.humanResourcesNoRationale ||
                        "Explain why human resources does not apply"
                      }
                    />
                  </Grid>
                )}
                {sectionVisible(formData.humanResourcesApplicable) && (
                  <>
                    <Grid item xs={12} md={8}>
                      <FormControl fullWidth>
                        <InputLabel>Human oversight</InputLabel>
                        <Select
                          label="Human oversight"
                          value={formData.humanOversight}
                          onChange={(e) =>
                            setField("humanOversight", e.target.value)
                          }
                        >
                          {HUMAN_OVERSIGHT_OPTIONS.map((o) => (
                            <MenuItem key={o} value={o}>
                              {o}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Human oversight details"
                        value={formData.humanOversightDetails}
                        onChange={(e) =>
                          setField("humanOversightDetails", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <MultiSelect
                        label="AI incident / disruption notification roles"
                        options={INCIDENT_NOTIFY_ROLES}
                        value={formData.incidentNotifyRoles}
                        onChange={(v) => setField("incidentNotifyRoles", v)}
                      />
                    </Grid>
                    {formData.incidentNotifyRoles?.includes("Other") && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Other incident notification role"
                          value={formData.otherIncidentNotifyRole || ""}
                          onChange={(e) =>
                            setField("otherIncidentNotifyRole", e.target.value)
                          }
                          error={Boolean(errors.otherIncidentNotifyRole)}
                          helperText={
                            errors.otherIncidentNotifyRole ||
                            "Describe the role when Other is selected"
                          }
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <MultiSelect
                        label="Operational roles"
                        options={GOVERNANCE_ROLE_OPTIONS}
                        value={
                          Array.isArray(formData.operationalRoles)
                            ? formData.operationalRoles
                            : []
                        }
                        onChange={(v) => setField("operationalRoles", v)}
                        error={errors.operationalRoles}
                        helperText={errors.operationalRoles}
                      />
                    </Grid>
                    {formData.operationalRoles?.includes("Other") && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Other operational role"
                          value={formData.otherOperationalRole || ""}
                          onChange={(e) =>
                            setField("otherOperationalRole", e.target.value)
                          }
                          error={Boolean(errors.otherOperationalRole)}
                          helperText={
                            errors.otherOperationalRole ||
                            "People/roles accountable for day-to-day operation"
                          }
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <MultiSelect
                        label="Technical / development roles"
                        options={GOVERNANCE_ROLE_OPTIONS}
                        value={
                          Array.isArray(formData.technicalDevelopmentRoles)
                            ? formData.technicalDevelopmentRoles
                            : []
                        }
                        onChange={(v) =>
                          setField("technicalDevelopmentRoles", v)
                        }
                        error={errors.technicalDevelopmentRoles}
                        helperText={errors.technicalDevelopmentRoles}
                      />
                    </Grid>
                    {formData.technicalDevelopmentRoles?.includes("Other") && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Other technical / development role"
                          value={formData.otherTechnicalDevelopmentRole || ""}
                          onChange={(e) =>
                            setField(
                              "otherTechnicalDevelopmentRole",
                              e.target.value,
                            )
                          }
                          error={Boolean(errors.otherTechnicalDevelopmentRole)}
                          helperText={
                            errors.otherTechnicalDevelopmentRole ||
                            "Describe the role when Other is selected"
                          }
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                      <MultiSelect
                        label="Governance / compliance roles"
                        options={GOVERNANCE_ROLE_OPTIONS}
                        value={
                          Array.isArray(formData.governanceComplianceRoles)
                            ? formData.governanceComplianceRoles
                            : []
                        }
                        onChange={(v) =>
                          setField("governanceComplianceRoles", v)
                        }
                        error={errors.governanceComplianceRoles}
                        helperText={errors.governanceComplianceRoles}
                      />
                    </Grid>
                    {formData.governanceComplianceRoles?.includes("Other") && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Other governance / compliance role"
                          value={formData.otherGovernanceComplianceRole || ""}
                          onChange={(e) =>
                            setField(
                              "otherGovernanceComplianceRole",
                              e.target.value,
                            )
                          }
                          error={Boolean(errors.otherGovernanceComplianceRole)}
                          helperText={
                            errors.otherGovernanceComplianceRole ||
                            "Describe the role when Other is selected"
                          }
                        />
                      </Grid>
                    )}
                    {[
                      [
                        "trainingCompetenceRequirements",
                        "Training & competence requirements",
                      ],
                      [
                        "safetySecurityPrivacyResponsibilities",
                        "Safety, security & privacy responsibilities",
                      ],
                    ].map(([field, label]) => (
                      <Grid item xs={12} key={field}>
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label={label}
                          value={formData[field]}
                          onChange={(e) => setField(field, e.target.value)}
                          error={Boolean(errors[field])}
                          helperText={errors[field]}
                        />
                      </Grid>
                    ))}
                    <Grid item xs={12} md={6}>
                      <PeopleMultiSelect
                        label="Authority to suspend / decommission"
                        options={people}
                        value={formData.authorityToSuspend}
                        onChange={(v) => setField("authorityToSuspend", v)}
                        loading={peopleLoading}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <PeopleMultiSelect
                        label="Final authorisation for use"
                        options={people}
                        value={formData.finalAuthorisation}
                        onChange={(v) => setField("finalAuthorisation", v)}
                        loading={peopleLoading}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            )}

            {tab === 6 && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Risk assessment</InputLabel>
                    <Select
                      label="Risk assessment"
                      value={formData.riskAssessment}
                      onChange={(e) =>
                        setField("riskAssessment", e.target.value)
                      }
                    >
                      {ASSESSMENT_STATUS_OPTIONS.map((o) => (
                        <MenuItem key={o} value={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Risk assessment ref."
                    value={formData.riskAssessmentRef}
                    onChange={(e) =>
                      setField("riskAssessmentRef", e.target.value)
                    }
                    error={Boolean(errors.riskAssessmentRef)}
                    helperText={errors.riskAssessmentRef}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Aegis risk ref."
                    value={formData.aegisRiskRef}
                    onChange={(e) => setField("aegisRiskRef", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>AI impact assessment</InputLabel>
                    <Select
                      label="AI impact assessment"
                      value={formData.aiImpactAssessment}
                      onChange={(e) =>
                        setField("aiImpactAssessment", e.target.value)
                      }
                    >
                      {ASSESSMENT_STATUS_OPTIONS.map((o) => (
                        <MenuItem key={o} value={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Impact assessment ref."
                    value={formData.impactAssessmentRef}
                    onChange={(e) =>
                      setField("impactAssessmentRef", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={formData.approvalStatus}
                      onChange={(e) =>
                        setField("approvalStatus", e.target.value)
                      }
                    >
                      {APPROVAL_STATUS_OPTIONS.map((o) => (
                        <MenuItem key={o} value={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    freeSolo
                    options={people}
                    getOptionLabel={(o) =>
                      typeof o === "string" ? o : o.label || ""
                    }
                    value={formData.approvedByName}
                    onChange={(_, v) => {
                      if (typeof v === "string") {
                        setField("approvedByName", v);
                        setField("approvedById", "");
                      } else {
                        setField("approvedByName", v?.label || "");
                        setField("approvedById", v?.id || "");
                      }
                    }}
                    onInputChange={(_, v) => setField("approvedByName", v)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Approved by"
                        error={Boolean(errors.approvedByName)}
                        helperText={errors.approvedByName}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Approval date"
                    value={formData.approvalDate}
                    onChange={(v) => setField("approvalDate", v)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: Boolean(errors.approvalDate),
                        helperText: errors.approvalDate,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Review date"
                    value={formData.reviewDate}
                    onChange={(v) => setField("reviewDate", v)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: Boolean(errors.reviewDate),
                        helperText: errors.reviewDate,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Conditions / restrictions"
                    value={formData.conditionsRestrictions}
                    onChange={(e) =>
                      setField("conditionsRestrictions", e.target.value)
                    }
                    error={Boolean(errors.conditionsRestrictions)}
                    helperText={errors.conditionsRestrictions}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          mt={2}
          pt={1}
          flexWrap="wrap"
          borderTop={1}
          borderColor="divider"
        >
          <Box display="flex" gap={1}>
            <Button onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={handleSaveDraft}
              disabled={submitting}
            >
              Save draft
            </Button>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              type="button"
              onClick={() => setTab((prev) => Math.max(prev - 1, 0))}
              disabled={submitting || tab === 0}
              startIcon={<BackIcon />}
            >
              Back
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={() =>
                setTab((prev) => Math.min(prev + 1, AI_SYSTEM_TAB_COUNT - 1))
              }
              disabled={submitting || tab === AI_SYSTEM_TAB_COUNT - 1}
              endIcon={<NextIcon />}
            >
              Next
            </Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {aiSystem && aiSystem.recordStatus !== "Draft"
                ? "Update"
                : "Create"}
            </Button>
          </Box>
        </Box>
      </Box>
  );
};

export default AiSystemForm;
